import { parseAsNativeArrayOf, parseAsString, useQueryState } from "nuqs";
import { useMemo, useCallback } from "react";

import {
  ArchitectureTypeSize,
  type ArchitectureTypeSeries,
} from "../../../../../../../__generated__/gql/graphql";
import { ARCHITECTURE_FILTER_OPTIONS } from "../../../../../reservation-filters/architecture-filter-options";

/** URL パラメータ名 */
const PARAM_NAME = "arch";

/** Large フィルタの値（シリーズとは別の AND 条件として扱う） */
const LARGE_VALUE = ArchitectureTypeSize.Large;

/**
 * 建築タイプフィルタアイテムの定義
 * Large は別セクションで表示するが、同じ Set で管理する
 */
export const architectureFilterItems = ARCHITECTURE_FILTER_OPTIONS;

/** Large フィルタの値を外部に公開 */
export const ARCHITECTURE_LARGE_VALUE = LARGE_VALUE;

/** フィルタに Large 条件が含まれるかを判定 */
export function isLargeFilter(filter: ReadonlySet<string>): boolean {
  return filter.has(LARGE_VALUE);
}

// nuqs parser for architecture type filter (?arch=MOSS&arch=Large)
const architectureFilterParser = parseAsNativeArrayOf(parseAsString);

/**
 * 宿泊施設の建築タイプがフィルタ条件に一致するかどうかを判定
 *
 * シリーズフィルタ（OR検索）:
 * - フィルタが空の場合は全て一致
 * - "Selection": series が "Selection" の場合に一致
 * - それ以外: seriesShortName と一致する場合に一致
 *
 * Largeフィルタ（AND条件）:
 * - filter に "Large" が含まれる場合、size === "Large" のみ一致
 */
function matchesArchitecture(
  architectureType: {
    series: ArchitectureTypeSeries;
    seriesShortName: string;
    size?: ArchitectureTypeSize | null;
  },
  filter: ReadonlySet<string>,
): boolean {
  const hasLarge = filter.has(LARGE_VALUE);
  if (hasLarge && architectureType.size !== ArchitectureTypeSize.Large)
    return false;

  const seriesCount = filter.size - (hasLarge ? 1 : 0);
  if (seriesCount === 0) return true;

  for (const value of filter) {
    if (value === LARGE_VALUE) continue;
    if (value === "Selection" && architectureType.series === "Selection")
      return true;
    if (architectureType.seriesShortName === value) return true;
  }
  return false;
}

/**
 * 建築タイプフィルタの状態管理とURL同期を行うカスタムフック
 *
 * - nuqs を使用して URL パラメータと状態を同期
 * - シリーズはOR検索、Largeは同じ Set 内に "Large" として含める
 */
export function useArchitectureTypeFilter() {
  const [urlFilter, setUrlFilter] = useQueryState(
    PARAM_NAME,
    architectureFilterParser,
  );

  // URL の文字列配列を Set<string> に変換。拠点ページCTA経由で付与される拠点固有の
  // 建築タイプ名（例: arch=CURRENT, arch=RIN）は ARCHITECTURE_FILTER_OPTIONS の静的
  // 選択肢に含まれないため、既知の値だけに絞らず空文字列のみを除外する。
  const architectureFilter = useMemo(
    () => new Set(urlFilter.filter((param) => param !== "")),
    [urlFilter],
  );

  const setArchitectureFilter = useCallback(
    (filter: Set<string>) => {
      const filterArray = Array.from(filter);
      void setUrlFilter(filterArray.length > 0 ? filterArray : []);
    },
    [setUrlFilter],
  );

  const matchesArchitectureFilter = useCallback(
    (architectureType: {
      series: ArchitectureTypeSeries;
      seriesShortName: string;
      size?: ArchitectureTypeSize | null;
    }): boolean => {
      return matchesArchitecture(architectureType, architectureFilter);
    },
    [architectureFilter],
  );

  return {
    /** 現在の建築タイプフィルタ状態（"Large" を含む場合あり） */
    architectureFilter,
    /** フィルタ更新関数（状態とURLを同期更新） */
    setArchitectureFilter,
    /** 宿泊施設の建築タイプがフィルタ条件に一致するかを判定 */
    matchesArchitectureFilter,
  };
}
